import { IconSearch } from "@tabler/icons-react";
import AppDialog from "components/common/AppDialog";
import { AdvancedConfirmDialog, NoData, Pagination, StyledTableHead, TableContainer } from "components";
import { LangConstant } from "const";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccountList, useChannelAddMember } from "hooks";

const MAX_ADD_MEMBER = 100;
const PAGE_SIZE = 10;

const AddChannelMemberDialog = ({ open, groupId, onClose, onRefreshMemberList, groupName, groupType, zIndex }) => {
  const { t: getLabel } = useTranslation();
  const labels = getLabel(LangConstant.OBJ_ADD_CHANNEL_MEMBER_DIALOG, { returnObjects: true });

  const isChannel = groupType !== 1;

  const dialogTitle = isChannel
    ? `Thêm người dùng vào kênh: ${groupName || ""}`
    : `Thêm người dùng vào nhóm: ${groupName || ""}`;

  const { accounts, totalPages, isFetching, handleSearch, handlePageChange, handleResetQuery } = useAccountList({ groupId });
  const { addChannelMembers, isLoading: isAdding } = useChannelAddMember();

  const [selectedIds, setSelectedIds] = useState([]);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const columns = [
    { label: "", align: "center", width: "5%" },
    { label: labels.colFullName, align: "left" },
    { label: labels.colUnit, align: "left", width: "18%" },
    { label: labels.colDepartment, align: "left", width: "20%" },
    { label: labels.colPosition, align: "left", width: "15%" },
  ];

  const handleSelectUser = useCallback(id => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  }, []);

  const handlePageClick = (e, page) => {
    setCurrentPage(page);
    handlePageChange(page);
  };

  const handleConfirm = () => {
    addChannelMembers({
      groupId,
      memberIds: selectedIds,
      onSuccess: () => {
        setIsOpenConfirm(false);
        handleResetQuery();
        onRefreshMemberList();
        setSelectedIds([]);
        setCurrentPage(1);
        onClose();
      },
      onError: handleOpenConfirm(false),
    });
  };

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  const handleOpenConfirm = isOpen => () => {
    setIsOpenConfirm(isOpen);
  };

  const handleSearchChange = e => {
    handleSearch(e.target.value);
    setCurrentPage(1);
  };

  return (
    <AppDialog
      open={open}
      title={dialogTitle}
      onClose={handleClose}
      style={{ width: 1200, maxWidth: "95vw" }}
      zIndex={zIndex || 1055}
    >
      <div className="mb-3" style={{ marginTop: 8 }}>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder={labels.searchPlaceholder}
            onChange={handleSearchChange}
          />
          <span className="input-group-text">
            <IconSearch size={16} />
          </span>
        </div>
      </div>

      <p style={{ fontSize: 16, fontWeight: 700, margin: "8px 0" }}>
        {labels.selectedCount}: <span style={{ fontWeight: 400 }}>{selectedIds.length}</span>
      </p>

      {/* User table */}
      <TableContainer>
        <table className="table table-vcenter table-hover">
          <StyledTableHead columns={columns} />
          <tbody>
            {accounts.map(user => {
              const isChecked = selectedIds.includes(user.id);
              const isDisableCheckbox = !isChecked && selectedIds.length >= MAX_ADD_MEMBER;
              return (
                <tr
                  key={user.id}
                  onClick={() => (isDisableCheckbox ? {} : handleSelectUser(user.id))}
                  style={{ cursor: "pointer" }}
                  role="checkbox"
                  aria-checked={isChecked}
                >
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      disabled={isDisableCheckbox}
                      checked={isChecked}
                      readOnly
                    />
                  </td>
                  <td className="text-left">{user.name}</td>
                  <td className="text-left">{user.unitName || "--"}</td>
                  <td className="text-left">{user.departmentName || "--"}</td>
                  <td className="text-left">{user.positionName || "--"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {accounts.length === 0 && !isFetching && <NoData />}
      </TableContainer>

      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageClick} />
      )}

      {/* Actions */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-link link-secondary" onClick={handleClose}>
          {labels.cancel}
        </button>
        <button
          className="btn btn-primary"
          disabled={selectedIds.length === 0 || isAdding}
          onClick={handleOpenConfirm(true)}
        >
          {labels.addMember}
        </button>
      </div>

      {isOpenConfirm && (
        <AdvancedConfirmDialog
          open
          title={getLabel(isChannel ? "TXT_ADD_CHANNEL_MEMBER" : "TXT_ADD_GROUP_MEMBER") || (isChannel ? "Thêm người dùng vào kênh" : "Thêm người dùng vào nhóm")}
          content={getLabel(isChannel ? "TXT_ADD_CHANNEL_MEMBER_CONTENT" : "TXT_ADD_GROUP_MEMBER_CONTENT") || (isChannel ? "Bạn muốn thêm người dùng này vào kênh?" : "Bạn muốn thêm người dùng này vào nhóm?")}
          submitProps={{ submitText: getLabel("L_CONFIRM_BUTTON"), onClick: handleConfirm }}
          cancelProps={{ onClick: handleOpenConfirm(false) }}
        />
      )}
    </AppDialog>
  );
};

export default AddChannelMemberDialog;
